import React from 'react';
import moment from 'moment';
import { QuickStatCell, AccountRow } from './ProfileCommonComponents';
import { ghostButtonStyle, formatTotalPaid } from './utils';
import { QUICK_STATS } from './constants';
import { COLORS } from 'components/UserDashboardV2/utils/constants';
import { fluid } from 'components/UserDashboardV2/utils/responsive';

const BasicDetailsSidebar = ({
    sidebarRef,
    isStaff,
    isTeacher,
    isStaffRoute,
    specificUserProfile,
    staffProfile,
    studentSidebar,
    userRowDetails,
}) => {
    // A fee record / installment exists to show (and remind about) — same
    // check that decides between the reminder text and "No fee record found."
    const feeReminderData = studentSidebar?.feeReminder;
    const hasFeeRecord = Boolean(
        feeReminderData && (
            feeReminderData.name
            || feeReminderData.amount != null
            || feeReminderData.dueDate
            || feeReminderData.installmentDetails
            || feeReminderData.totalPayable
        )
    );

    return (
        <div ref={sidebarRef} style={{ flex: `0 0 ${fluid(598)}`, minWidth: 0, display: "flex", flexDirection: "column", gap: fluid(20) }}>
            {/* Quick stats */}
            <div style={{
                backgroundColor: "#967ED3",
                borderRadius: fluid(24),
                padding: fluid(20),
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                gap: fluid(16),
            }}>
                <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 500, fontSize: fluid(20), letterSpacing: 0, color: "#FFFFFF" }}>
                    Quick stats
                </span>
                {isStaff ? (
                    (() => {
                        const staffQuickStats = specificUserProfile?.quickStats || staffProfile?.quickStats;
                        const attendanceVal = staffQuickStats?.attendance != null ? (String(staffQuickStats?.attendance).endsWith('%') ? String(staffQuickStats?.attendance) : `${staffQuickStats?.attendance}%`)
                            : "0%";
                        const testsConductedVal = (staffQuickStats?.testsConducted ?? staffQuickStats?.testConducted) != null
                            ? String(staffQuickStats.testsConducted ?? staffQuickStats.testConducted)
                            : "0";

                        return isTeacher ? (
                            <div style={{ display: "flex", gap: fluid(16) }}>
                                <QuickStatCell label="Attendance" value={attendanceVal} />
                                <QuickStatCell label="Tests completed" value={testsConductedVal} />
                            </div>
                        ) : (
                            <div style={{ display: "flex", gap: fluid(16) }}>
                                <QuickStatCell label="Attendance" value={attendanceVal} style={{ maxWidth: "calc(50% - 8px)" }} />
                            </div>
                        );
                    })()
                ) : (
                    (() => {
                        const studentQuickStats = studentSidebar?.quickStats || specificUserProfile?.quickStats;
                        const activeCoursesVal = studentQuickStats?.activeCourses != null
                            ? String(studentQuickStats.activeCourses)
                            : (!isStaffRoute && QUICK_STATS[0]?.value ? QUICK_STATS[0].value : "0");
                        const attendanceRaw = studentQuickStats?.attendance;
                        const attendanceVal = attendanceRaw != null
                            ? (String(attendanceRaw).endsWith('%') ? String(attendanceRaw) : `${attendanceRaw}%`)
                            : "0%";
                        const totalPaidVal = studentQuickStats?.totalPaid != null
                            ? formatTotalPaid(studentQuickStats.totalPaid)
                            : "₹0";
                        const testsTakenVal = (studentQuickStats?.testsTaken ?? studentQuickStats?.testTaken) != null
                            ? String(studentQuickStats.testsTaken ?? studentQuickStats.testTaken)
                            : "0";

                        const quickStatsList = [
                            { label: "Active courses", value: activeCoursesVal },
                            { label: "Attendance", value: attendanceVal },
                            { label: "Total paid", value: totalPaidVal },
                            { label: "Tests taken", value: testsTakenVal },
                        ];

                        return (
                            <div style={{ display: "flex", flexDirection: "column", gap: fluid(16) }}>
                                <div style={{ display: "flex", gap: fluid(16) }}>
                                    {quickStatsList.slice(0, 2).map(({ label, value }) => (
                                        <QuickStatCell key={label} label={label} value={value} />
                                    ))}
                                </div>
                                <div style={{ display: "flex", gap: fluid(16) }}>
                                    {quickStatsList.slice(2, 4).map(({ label, value }) => (
                                        <QuickStatCell key={label} label={label} value={value} />
                                    ))}
                                </div>
                            </div>
                        );
                    })()
                )}
            </div>

            {/* Login & account */}
            <div style={{
                backgroundColor: "#FFFFFF",
                borderRadius: fluid(24),
                padding: fluid(16),
                display: "flex",
                flexDirection: "column",
                gap: fluid(20),
                boxSizing: "border-box",
            }}>
                <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 500, fontSize: fluid(20), letterSpacing: 0, lineHeight: fluid(20), color: "#191919" }}>
                    Login & account
                </span>
                {(() => {
                    const activeLoginAndAccount = isStaffRoute
                        ? (staffProfile?.loginAndAccount || specificUserProfile?.loginAndAccount)
                        : studentSidebar?.loginAndAccount;

                    const joinedVal = activeLoginAndAccount?.joined
                        ? moment(activeLoginAndAccount.joined).format('DD MMM YYYY')
                        : (userRowDetails?.joinedOn ? moment(userRowDetails.joinedOn).format('DD MMM YYYY') : null);

                    const lastLoginVal = activeLoginAndAccount?.lastLogin
                        ? moment(activeLoginAndAccount.lastLogin).format('DD MMM YYYY, hh:mm A')
                        : null;

                    const pushOs = (
                        activeLoginAndAccount?.pushOs ??
                        staffProfile?.pushOs ??
                        specificUserProfile?.pushOs ??
                        staffProfile?.data?.pushOs ??
                        specificUserProfile?.data?.pushOs ??
                        studentSidebar?.pushOs ??
                        studentSidebar?.loginAndAccount?.pushOs ??
                        userRowDetails?.pushOs
                    );

                    const appVersion = (
                        activeLoginAndAccount?.appVersion ??
                        staffProfile?.loginAndAccount?.appVersion ??
                        specificUserProfile?.loginAndAccount?.appVersion ??
                        studentSidebar?.loginAndAccount?.appVersion ??
                        staffProfile?.appVersion ??
                        specificUserProfile?.appVersion ??
                        staffProfile?.data?.appVersion ??
                        specificUserProfile?.data?.appVersion ??
                        studentSidebar?.appVersion ??
                        userRowDetails?.appVersion
                    );

                    const cleanPushOs = (pushOs != null && String(pushOs).trim().toLowerCase() !== "null" && String(pushOs).trim().toLowerCase() !== "undefined")
                        ? String(pushOs).trim()
                        : "";

                    const formatOs = (os) => {
                        if (!os) return "";
                        const lower = os.toLowerCase();
                        if (lower === "ios") return "iOS";
                        if (lower === "android") return "Android";
                        return os;
                    };

                    const formatVersion = (ver) => {
                        if (ver == null) return "";
                        const trimmed = String(ver).trim();
                        if (!trimmed || trimmed.toLowerCase() === "null" || trimmed.toLowerCase() === "undefined") return "";
                        return /^\d/.test(trimmed) ? `v${trimmed}` : trimmed;
                    };

                    const formattedOs = formatOs(cleanPushOs);
                    const formattedVer = formatVersion(appVersion);

                    let appInstalledVal = "NA";
                    if (formattedOs && formattedVer) {
                        appInstalledVal = `${formattedOs} · ${formattedVer}`;
                    } else if (formattedOs) {
                        appInstalledVal = formattedOs;
                    } else if (formattedVer) {
                        appInstalledVal = formattedVer;
                    }

                    return (
                        <>
                            <AccountRow label="Joined" value={joinedVal} />
                            <AccountRow label="Last login" value={lastLoginVal} />
                            <AccountRow label="App installed" value={appInstalledVal} />
                        </>
                    );
                })()}
            </div>

            {/* Fee reminder — students only */}
            {!isStaff && (
                <div style={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: fluid(24),
                    padding: fluid(20),
                    display: "flex",
                    flexDirection: "column",
                    gap: fluid(16),
                    boxSizing: "border-box",
                }}>
                    <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 500, fontSize: fluid(20), letterSpacing: 0, lineHeight: fluid(20), color: "#191919" }}>
                        Fee reminder
                    </span>
                    {(() => {
                        const feeReminder = studentSidebar?.feeReminder;
                        if (!hasFeeRecord) {
                            return (
                                <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, fontSize: fluid(16), letterSpacing: 0, lineHeight: fluid(20), color: "#666666" }}>
                                    No fee record found.
                                </span>
                            );
                        }
                        if (feeReminder.name || feeReminder.amount != null || feeReminder.dueDate) {
                            const name = feeReminder.name || "Fee";
                            const amount = feeReminder.amount != null ? Number(feeReminder.amount).toLocaleString('en-IN') : "0";
                            const dueDateFormatted = feeReminder.dueDate && moment(feeReminder.dueDate).isValid()
                                ? moment(feeReminder.dueDate).format("DD MMM YYYY")
                                : null;

                            return (
                                <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, fontSize: fluid(16), letterSpacing: 0, lineHeight: fluid(20), color: "#666666" }}>
                                    {name} of ₹{amount}{dueDateFormatted ? " due on " : ""}
                                    {dueDateFormatted && (
                                        <span style={{ fontWeight: 700, color: "#191919" }}>
                                            {dueDateFormatted}
                                        </span>
                                    )}
                                </span>
                            );
                        }
                        return (
                            <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, fontSize: fluid(16), letterSpacing: 0, lineHeight: fluid(20), color: "#666666" }}>
                                {feeReminder.installmentDetails || `Total payable: ₹${feeReminder.totalPayable}`}
                            </span>
                        );
                    })()}
                    {/* Only when there's a fee record / installment to remind about. */}
                    {hasFeeRecord && (
                        <button
                            type="button"
                            title="Fee reminders aren't wired up yet"
                            style={{ ...ghostButtonStyle(COLORS.primary), width: fluid(212) }}
                        >
                            SEND REMINDER
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default BasicDetailsSidebar;
